import styled from '@emotion/styled';
import { RoomEventDto, SpeakingSessionRoomDto } from 'api/planning';
import { Icon } from '@britishcouncil/react-admin';
import { RoomStructure } from './RoomStructure';
import { useMemo } from 'react';
import { EditRoomBar } from './EditRoomBar';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { Loader, Dimmer, Modal, Button, Popup } from 'semantic-ui-react';
import { generatePath, Link } from 'react-router';
import classNames from 'classnames';
import { SpeakingSlotTypesIndicator } from 'components';
import { useRoom } from './useRoom';
import { RoomAllocationPreview } from './RoomAllocationPreview';
import { ExaminerAllocationChangedEmailNotificationMessage } from '../examiners/EmailNotificationMessage';

type Props = {
  room: SpeakingSessionRoomDto;
  roomsEvents: RoomEventDto[];
};

export const Room = ({ room, roomsEvents }: Props) => {
  const {
    speakingSessionDate,
    localSpeakingSlotMinutesOffset,
    editSpeakingSessionRoom,
    removeSpeakingSessionRoom,
    expanded,
    expandable,
    editing,
    saving,
    scrollIntoViewRef,
    gridRef,
    speakingSlotTypes,
    sessionId,
    roomStructures,
    clashedPeriods,
    addRoomStructure,
    changeRoomStructure,
    deleteRoomStructure,
    saveRoom,
    handleExpandClick,
    selectedSlotAction,
    setSelectedSlotAction,
    resetEditRoomMutationState,
    showConfirmModal,
    affectedExaminers,
    confirmSave,
    cancelSave,
  } = useRoom({ room, roomsEvents });

  const slots = useMemo(
    () =>
      roomStructures.map((x) => (
        <RoomStructure
          key={`room-structure-${x.id}`}
          sessionRoomId={room.id}
          structure={x}
          structures={roomStructures}
          editing={editing}
          selectedSlotAction={selectedSlotAction}
          onDelete={deleteRoomStructure}
          onChangeStructure={changeRoomStructure}
          localSpeakingSlotMinutesOffset={localSpeakingSlotMinutesOffset}
          speakingSessionDate={speakingSessionDate}
          clashedPeriods={clashedPeriods}
          roomCapacity={room.capacity}
        />
      )),
    [
      roomStructures,
      editing,
      selectedSlotAction,
      deleteRoomStructure,
      changeRoomStructure,
      clashedPeriods,
      room.capacity,
      localSpeakingSlotMinutesOffset,
    ]
  );

  const onManageSlotsClick = () => {
    resetEditRoomMutationState();
  };

  return (
    <S.Subgrid
      className={classNames(
        { expanded },
        { editing },
        { expandable },
        {
          [`action-${SpeakingSlotAction[selectedSlotAction]}`]: editing,
        }
      )}
      ref={scrollIntoViewRef}
    >
      <S.Header>
        <S.RowLeft onClick={handleExpandClick} data-testid="room-expand">
          <Icon name={expanded ? 'caret up' : 'caret right'} />
          <SpeakingSlotTypesIndicator speakingSlotTypes={speakingSlotTypes} />
          <S.Text>{room.name}</S.Text>
        </S.RowLeft>
        <S.RowCenter ref={gridRef}>
          <RoomAllocationPreview
            structures={roomStructures}
            localSpeakingSlotMinutesOffset={localSpeakingSlotMinutesOffset}
            clashedPeriods={clashedPeriods}
          />
        </S.RowCenter>
        <S.RowRight>
          {!editing ? (
            <S.Popup
              trigger={
                <S.TriggerWrapper>
                  <Icon name="ellipsis vertical" data-testid="more-options" />
                </S.TriggerWrapper>
              }
              content={
                <S.PopupContent>
                  {room.canEdit ? (
                    <S.ClickablePopupItem
                      to={generatePath(editSpeakingSessionRoom.absolutePath, {
                        sessionId,
                      })}
                      state={{
                        roomId: room.id,
                      }}
                      onClick={onManageSlotsClick}
                      data-testid="edit-room"
                    >
                      <Icon name="pencil" />
                      Manage slots
                    </S.ClickablePopupItem>
                  ) : (
                    <S.NonClickablePopupItem data-testid="edit-room">
                      <Icon name="pencil" />
                      Manage slots
                    </S.NonClickablePopupItem>
                  )}

                  {room.canRemove ? (
                    <S.ClickablePopupItem
                      to={generatePath(removeSpeakingSessionRoom.absolutePath, {
                        sessionId,
                        sessionRoomId: room.id,
                      })}
                      data-testid="remove-room"
                      className="red-item"
                    >
                      <Icon name="trash" />
                      Delete room
                    </S.ClickablePopupItem>
                  ) : (
                    <S.NonClickablePopupItem data-testid="remove-room" className="red-item">
                      <Icon name="trash" />
                      Delete room
                    </S.NonClickablePopupItem>
                  )}
                </S.PopupContent>
              }
              position="bottom left"
              on="click"
              hideOnScroll
            />
          ) : null}
        </S.RowRight>
      </S.Header>
      <Dimmer active={editing && saving} inverted>
        <Loader active />
      </Dimmer>
      {expanded ? <S.Content>{slots}</S.Content> : null}
      {editing ? (
        <EditRoomBar
          onAddRow={addRoomStructure}
          onSave={saveRoom}
          selectedSlotAction={selectedSlotAction}
          setSelectedSlotAction={setSelectedSlotAction}
          availableSpeakingSlotTypes={room.availableSpeakingSlotTypes}
          saving={saving}
        />
      ) : null}

      <Modal open={showConfirmModal} size="tiny">
        <Modal.Header>Examiner allocation has changed</Modal.Header>
        <Modal.Content>
          <ExaminerAllocationChangedEmailNotificationMessage examinerNames={affectedExaminers} />
        </Modal.Content>
        <Modal.Actions>
          <Button compact secondary onClick={cancelSave}>
            Cancel
          </Button>
          <Button compact color="green" onClick={confirmSave}>
            Continue
          </Button>
        </Modal.Actions>
      </Modal>
    </S.Subgrid>
  );
};

const S = {
  Text: styled.div`
    max-width: 200px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  Popup: styled(Popup)`
    &.ui.popup {
      padding: 0 !important;
      border: 1px solid #d4d4d5;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  `,
  TriggerWrapper: styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 3px;
    transition: background-color 0.2s ease;

    i.icon {
      margin: 0 !important;
      font-size: 18px !important;
      color: #607582;
    }
  `,
  PopupContent: styled.div`
    display: flex;
    flex-direction: column;
    min-width: 150px;
    padding: 0 !important;
  `,
  ClickablePopupItem: styled(Link)`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    border: none;
    background: none;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
      color: inherit;
      text-decoration: none;
    }

    &.red-item {
      color: #db2828;
    }

    i.icon {
      margin: 0 !important;
      font-size: 14px;
    }
  `,
  NonClickablePopupItem: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: not-allowed;
    border: none;
    background: none;
    text-decoration: none;
    color: inherit;
    opacity: 0.5;

    &.red-item {
      color: #db2828;
    }

    i.icon {
      margin: 0 !important;
      font-size: 14px;
    }
  `,
  Subgrid: styled.div`
    position: relative;
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    scroll-margin-top: 100px;
  `,
  Header: styled.div`
    position: sticky;
    top: 98px;
    background-color: #fafafb;
    z-index: 1;
    border: 1px solid #e2e2e3;
    background-color: #fafafb;
    border-radius: 5px;
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;

    .expanded > & {
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;
      border-bottom: unset;
    }
  `,
  Content: styled.div`
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    border-top: 1px solid #e2e2e3;

    & > div:last-of-type {
      & > div:last-of-type {
        border-bottom-right-radius: 5px;
      }
      & > div:first-of-type {
        border-bottom: 1px solid #e2e2e3;
      }
    }

    div:not(.editing) > & > div:last-of-type {
      & > div:first-of-type {
        border-bottom-left-radius: 5px;
      }
    }
  `,
  RowLeft: styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    font-weight: bold;
    font-size: 12px;
    padding: 10px;
    border-right: 0;
    color: #415561;
    .expandable:not(.editing) > div > & {
      cursor: pointer;
    }

    & > i.icon {
      margin: 0;
      font-size: 16px;
      color: #415561;
    }
  `,
  RowCenter: styled.div`
    overflow-x: auto;
    scrollbar-width: none;
    display: grid;
    grid-template-columns: minmax(2160px, 1fr);
    &:after {
      content: '';
      position: absolute;
      box-shadow:
        inset -10px 0 10px -10px #d5d5d5,
        inset 10px 0 10px -10px #d5d5d5;
      width: 100%;
      height: 100%;
      grid-column: 2 / 3;
      top: 0;
      pointer-events: none;
    }
  `,
  RowRight: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
};
